import { useState } from 'react';
import { CONTACT_REASONS, type ContactReason } from '@visiward/shared';
import { Button } from '@/components/ui/Button';
import { Input, Label, Textarea, Select } from '@/components/ui/Input';
import { Alert, Spinner } from '@/components/ui/misc';
import { Turnstile } from '@/components/Turnstile';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

export function ContactForm({ defaultReason }: { defaultReason?: ContactReason }) {
  const { user } = useAuth();
  const [name, setName] = useState(user?.displayName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [company, setCompany] = useState('');
  const [reason, setReason] = useState<ContactReason>(defaultReason ?? 'Support');
  const [message, setMessage] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!user && !token) {
      setError('Please complete the verification.');
      return;
    }
    setBusy(true);
    try {
      await api.sendContact({
        name,
        email,
        company: company || undefined,
        reason,
        message,
        turnstileToken: token ?? undefined,
      });
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send your message.');
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <Alert tone="success">
        Thanks — your message has been sent. We’ll get back to you at {email}.
      </Alert>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="c-name">Name</Label>
          <Input id="c-name" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div>
          <Label htmlFor="c-email">Email</Label>
          <Input
            id="c-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="c-company">Company (optional)</Label>
          <Input id="c-company" value={company} onChange={(e) => setCompany(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="c-reason">Reason</Label>
          <Select
            id="c-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value as ContactReason)}
          >
            {CONTACT_REASONS.map((rn) => (
              <option key={rn} value={rn}>
                {rn}
              </option>
            ))}
          </Select>
        </div>
      </div>
      <div>
        <Label htmlFor="c-message">Message</Label>
        <Textarea
          id="c-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          required
          placeholder="How can we help?"
        />
      </div>
      {!user && <Turnstile onToken={setToken} />}
      {error && <Alert tone="error">{error}</Alert>}
      <Button type="submit" className="w-full" disabled={busy}>
        {busy ? <Spinner /> : 'Send message'}
      </Button>
    </form>
  );
}
